import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../components/common/DatePicker';
import { useDispatch, useSelector } from 'react-redux';
import { updateSpinnerState } from '../../../actions/spinner';
import { updateToastInfo } from '../../../actions/settings';
import { returnWarehouseDropdownOptions } from '../../../utils/warehouse';
import EditableDropDown from '../../../components/common/EditableDropdown';
import { Button, Card, Form } from '@themesberg/react-bootstrap';
import {
  faArrowRotateLeft,
  faSliders,
  faTruckFast,
  faUserMinus,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TableOnly from '../../scheme-management/Components/IndividualScheme/TableOnly';
import { loaderActivityColumns } from './columns';
import { getLoaderActivity } from '../../../parse-functions/loaders';
import LoaderViewModal from './LoaderViewModal';

const LoaderActivity = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({});
  const [loaderData, setLoaderData] = useState({});
  const [loaderViewModal, setLoaderViewModal] = useState(false);
  const [selectedLoader, setSelectedLoader] = useState(null);
  const isAdmin = useSelector(
    (state) => state?.user?.preferences?.isAdmin || false
  );
  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );
  const storeName = useSelector(
    (state) => state?.user?.preferences?.storeName || ''
  );
  const dispatch = useDispatch();

  const handleApplyFilters = async () => {
    try {
      if (!formData.date || (!formData.warehouse && !storeName)) {
        throw new Error('Please select date and warehouse');
      }
      dispatch(updateSpinnerState(true));
      const data = await getLoaderActivity({
        date: formData.date,
        warehouse: formData.warehouse || storeName,
      });

      setLoaderData({
        ...data,
        currentLoaders: Object.values(data.loaderObj)
          .flatMap((item) => item.loader)
          .filter(Boolean),
      });
    } catch (error) {
      console.log(error);
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t(`Error while fetching loader activity.`),
          message: error.message,
        })
      );
    } finally {
      dispatch(updateSpinnerState(false));
    }
  };

  const handleViewClick = (row) => {
    setSelectedLoader({
      trips: loaderData?.loaderObj?.[row?.loader?.loaderCode]?.trips,
      currentLoader: row,
    });
    setLoaderViewModal(true);
  };

  console.log(loaderData);

  return (
    <div>
      <div className="mb-1 d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-2">
        <h4>{t('Loader Activity')}</h4>
      </div>
      <Card className="mb-2">
        <Card.Body className="p-2 fw-bold">Select Filters</Card.Body>
      </Card>
      <Card className="mb-2">
        <Card.Body className="p-2 ">
          <div className="d-flex gap-5 align-items-end">
            <Form.Group>
              <Form.Label>{t('Select Date')}</Form.Label>
              <DatePicker
                value={formData?.date}
                setValue={(date) => {
                  setFormData((prev) => ({ ...prev, date: date }));
                }}
                width="300px"
              />
            </Form.Group>
            {isAdmin && (
              <Form.Group>
                <Form.Label>{t('Select Warehouse')}</Form.Label>
                <EditableDropDown
                  width="300px"
                  onChange={(option) => {
                    setFormData((prev) => ({
                      ...prev,
                      warehouse: option?.value,
                    }));
                  }}
                  value={formData?.warehouse}
                  options={[
                    { value: 'All', label: 'All' },
                    ...returnWarehouseDropdownOptions(otherWarehouses),
                  ]}
                  placeholder={t('Select Warehouse')}
                  backgroundColor="#F0F0F0"
                />
              </Form.Group>
            )}
            <div className="d-flex gap-2 flex-end">
              <Button
                onClick={() => setFormData({})}
                variant="white"
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  background: '#EAEDF2',
                  marginInline: '10px',
                }}
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} />
                &nbsp;&nbsp; Reset
              </Button>

              <Button
                onClick={handleApplyFilters}
                variant="white"
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  border: '1px solid #262B40',
                  marginInline: '10px',
                }}
                className="me-2"
              >
                <FontAwesomeIcon icon={faSliders} /> &nbsp;&nbsp;Fetch Data
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-2 gap-3">
        <Card className="w-100">
          <Card.Body className="p-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <p className="mb-0">Active Loaders</p>
              <p className="mb-2">
                <span className="fw-bold">
                  {loaderData?.activeLoaders || 0}
                </span>{' '}
              </p>
              <p className="mb-0 fw-light fs-6">Marked In</p>
            </div>
            <FontAwesomeIcon icon={faUsers} className="text-danger" size="2x" />
          </Card.Body>
        </Card>
        <Card className="w-100">
          <Card.Body className="p-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <p className="mb-0">Assigned Loaders</p>
              <p className="mb-2">
                <span className="fw-bold">
                  {loaderData?.assignedLoaders || 0}
                </span>
              </p>
              <p className="mb-0 fw-light fs-6">Assigned to Trips</p>
            </div>
            <FontAwesomeIcon
              icon={faUserMinus}
              className="text-danger"
              size="2x"
            />
          </Card.Body>
        </Card>
        <Card className="w-100">
          <Card.Body className="p-2 d-flex justify-content-between align-items-center">
            <div className="d-flex flex-column">
              <p className="mb-0">Total Trips</p>
              <p className="mb-2">
                <span className="fw-bold">{loaderData?.totalTrips || 0}</span>
              </p>
              <p className="mb-0 fw-light fs-6">Dispatch count</p>
            </div>
            <FontAwesomeIcon
              icon={faTruckFast}
              className="text-danger"
              size="2x"
            />
          </Card.Body>
        </Card>
      </div>
      <Card className="mb-2">
        <Card.Body className="p-2 fw-bold">Loader Activity Overview</Card.Body>
      </Card>
      <TableOnly
        id={'loaderActivityTable'}
        columns={loaderActivityColumns(handleViewClick)}
        data={loaderData?.currentLoaders || []}
      />
      <LoaderViewModal
        show={loaderViewModal}
        handleClose={() => setLoaderViewModal(false)}
        loader={selectedLoader}
      />
    </div>
  );
};

export default LoaderActivity;
